import { motion } from 'framer-motion';

interface ChipStackProps {
  amount: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const CHIP_COLORS = [
  { value: 1, color: 'bg-white', border: 'border-slate-300', text: 'text-slate-800' },
  { value: 5, color: 'bg-red-500', border: 'border-red-600', text: 'text-white' },
  { value: 25, color: 'bg-green-500', border: 'border-green-600', text: 'text-white' },
  { value: 100, color: 'bg-black', border: 'border-slate-600', text: 'text-white' },
  { value: 500, color: 'bg-purple-500', border: 'border-purple-600', text: 'text-white' },
  { value: 1000, color: 'bg-yellow-500', border: 'border-yellow-600', text: 'text-slate-800' },
  { value: 5000, color: 'bg-orange-500', border: 'border-orange-600', text: 'text-white' },
];

function getChipConfig(amount: number) {
  for (let i = CHIP_COLORS.length - 1; i >= 0; i--) {
    if (amount >= CHIP_COLORS[i].value) {
      return CHIP_COLORS[i];
    }
  }
  return CHIP_COLORS[0];
}

function formatAmount(amount: number): string {
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`;
  }
  return amount.toString();
}

export default function ChipStack({ amount, size = 'md', className = '' }: ChipStackProps) {
  const chipConfig = getChipConfig(amount);

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm',
  };

  // 计算筹码堆叠高度 - 更紧凑
  const stackHeight = Math.min(Math.floor(amount / 200), 5);

  const getMarginTop = (idx: number, sz: 'sm' | 'md' | 'lg') => {
    if (idx === 0) return 0;
    // 更紧凑的堆叠
    if (sz === 'sm') return -4;
    if (sz === 'md') return -5;
    return -6;
  };

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      {/* 金额标签 - 放在上方 */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="mb-1"
      >
        <span className="bg-black/80 text-white text-xs px-1.5 py-0.5 rounded-full font-mono">
          {formatAmount(amount)}
        </span>
      </motion.div>

      {/* 筹码堆叠 */}
      <div className="relative flex flex-col items-center">
        {Array.from({ length: Math.max(1, stackHeight) }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: i * 0.03 }}
            className={`${sizeClasses[size]} ${chipConfig.color} ${chipConfig.border} border-2 rounded-full flex items-center justify-center shadow-sm`}
            style={{
              marginTop: getMarginTop(i, size),
              zIndex: stackHeight - i,
            }}
          >
            {/* 筹码图案 */}
            <div className={`w-1/2 h-1/2 border border-dashed ${chipConfig.border} rounded-full flex items-center justify-center`}>
              <span className={`${chipConfig.text} font-bold text-[8px]`}>♠</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
